/**
 * ReviewLinkedCard — the resources an operator has to follow to fix this
 * (BON-381): the booking record if one exists, the Stripe payment if an intent
 * was ever created, the customer, and the workshop (or the gift-pass purchase).
 *
 * The design's gift-card row is omitted (gift cards are the next project), and
 * a disabled row states WHY it is disabled rather than vanishing — "no booking
 * was created for this session" is itself the finding on a flagged checkout.
 */
import { CreditCard, Ticket, Users } from 'lucide-react';

import ReviewLinkRow from './components/ReviewLinkRow';
import {
  customerLabel,
  GIFT_PASS_PURCHASE_LABEL,
  passesLabel,
  stripeStatusLabel,
  venueLine,
  workshopLabel,
  workshopWhen,
} from './reviewQueueModel';
import type { ReviewDetail } from './reviewTypes';

export interface ReviewLinkedCardProps {
  detail: ReviewDetail;
}

/** Stripe's own status + card, or the honest reason there is nothing to say. */
function stripeHintFor(detail: ReviewDetail): string | null {
  if (detail.stripeUnavailable) return 'Stripe was unreachable — open it directly';
  if (!detail.stripe) return null;
  // The mapped label, matching the money card and the design ("Captured · Visa
  // ···· 4242"). This line was showing Stripe's raw enum, so one page said
  // "Captured" in one card and "succeeded" in another about the same payment.
  return [stripeStatusLabel(detail.stripe.status), detail.stripe.card].filter(Boolean).join(' · ');
}

export function ReviewLinkedCard({ detail }: ReviewLinkedCardProps) {
  const isBooking = detail.kind === 'booking';

  return (
    <section className="nb-card nb-card-tight" style={{ padding: 0, overflow: 'hidden' }}>
      <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--nb-border)' }}>
        <h3
          className="nb-display"
          style={{ margin: 0, fontSize: 15, color: 'var(--nb-bark-dark)' }}
        >
          Linked resources
        </h3>
      </div>

      {/* Omitted entirely on a checkout: there is no booking to link to, and a
          permanently-disabled row reading "No booking was created for this
          session" is a dead line in a list of live ones. The section heading
          already says "No booking was created", and the queue section this item
          came from says it too. */}
      {isBooking && (
        <ReviewLinkRow
          icon={<Ticket size={14} />}
          label="Booking record"
          value={detail.reference}
          to={`/admin/bookings/${detail.id}`}
        />
      )}
      <ReviewLinkRow
        icon={<CreditCard size={14} />}
        label="Stripe payment"
        value={detail.paymentIntentId ?? 'No payment intent was created'}
        hint={stripeHintFor(detail)}
        disabled={!detail.stripe}
        href={detail.stripe?.dashboardUrl}
      />
      <ReviewLinkRow
        icon={<Users size={14} />}
        label={customerLabel(detail.customer)}
        value={detail.customer.email ?? 'No email on file'}
        info
      />

      <div style={{ padding: '10px 14px' }}>
        <div
          style={{
            fontSize: 10.5,
            fontWeight: 700,
            letterSpacing: '.05em',
            textTransform: 'uppercase',
            color: 'var(--nb-subtle)',
            marginBottom: 5,
          }}
        >
          {detail.workshop ? 'Workshop' : 'Purchase'}
        </div>
        {detail.workshop ? (
          <>
            <div style={{ fontSize: 12.5, fontWeight: 700 }}>{workshopLabel(detail.workshop)}</div>
            <div style={{ fontSize: 11.5, color: 'var(--nb-subtle)' }}>
              {workshopWhen(detail.workshop)} · {venueLine(detail.workshop)}
            </div>
          </>
        ) : (
          <>
            <div style={{ fontSize: 12.5, fontWeight: 700 }}>{GIFT_PASS_PURCHASE_LABEL}</div>
            <div style={{ fontSize: 11.5, color: 'var(--nb-subtle)' }}>
              {passesLabel(detail.ticketCount)}
            </div>
          </>
        )}
      </div>
    </section>
  );
}
